import { useMemo, useCallback, useState } from "react";
import { AlertDialog, Button, Flex } from "@radix-ui/themes";

export enum DialogMode {
  CANCEL = "cancel",
  UPDATE = "update",
  FINISH = "finish",
  NONE = "none",
}

const DIALOG_CONFIG = {
  [DialogMode.CANCEL]: {
    title: "Cancel annotations?",
    description: "You will lose all unsaved changes.",
    cancelText: "Continue editing",
    confirmText: "Discard changes",
    confirmColor: "red" as const,
  },
  [DialogMode.UPDATE]: {
    title: "Update annotations?",
    description: "This will allow you to modify the current annotations.",
    cancelText: "Cancel",
    confirmText: "Start editing",
    confirmColor: "red" as const,
  },
  [DialogMode.FINISH]: {
    title: "Save annotations?",
    description: "This will save all your changes.",
    cancelText: "Continue editing",
    confirmText: "Save",
    confirmColor: "red" as const,
  },
  [DialogMode.NONE]: {
    title: "",
    description: "",
    cancelText: "",
    confirmText: "",
    confirmColor: "gray" as const,
  },
};

interface AnnotationActionButtonsProps {
  isEditable: boolean;
  isRequestLoading: boolean;
  isOrderLoading: boolean;
  isAnnotationSaving: boolean;
  onCancel: () => void;
  onUpdate: () => void;
  onFinish: () => void;
}

export function AnnotationActionButtons({
  isEditable,
  isRequestLoading,
  isOrderLoading,
  isAnnotationSaving,
  onCancel,
  onUpdate,
  onFinish,
}: AnnotationActionButtonsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.NONE);

  const openDialog = useCallback((mode: DialogMode) => {
    setDialogMode(mode);
    setDialogOpen(true);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Reset mode after dialog closes to prevent content flash
      setTimeout(() => {
        setDialogMode(DialogMode.NONE);
      }, 300); // Delay to match animation duration
    }
  }, []);

  const handleAction = useCallback(() => {
    switch (dialogMode) {
      case DialogMode.CANCEL:
        onCancel();
        break;
      case DialogMode.UPDATE:
        onUpdate();
        break;
      case DialogMode.FINISH:
        onFinish();
        break;
    }
    setDialogOpen(false);
  }, [dialogMode, onCancel, onUpdate, onFinish]);

  // Get current dialog configuration
  const currentConfig = useMemo(() => DIALOG_CONFIG[dialogMode], [dialogMode]);

  return (
    <>
      <Button
        size="1"
        variant="soft"
        color="gray"
        mr="1"
        onClick={() =>
          openDialog(isEditable ? DialogMode.CANCEL : DialogMode.UPDATE)
        }
      >
        {isEditable ? "Cancel" : "Update"}
      </Button>

      <Button
        size="1"
        variant="soft"
        color="gray"
        mr="1"
        loading={isRequestLoading}
        onClick={() => openDialog(DialogMode.FINISH)}
        disabled={
          !isEditable ||
          isRequestLoading ||
          isOrderLoading ||
          isAnnotationSaving
        }
      >
        Save
      </Button>

      <AlertDialog.Root open={dialogOpen} onOpenChange={handleOpenChange}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>{currentConfig.title}</AlertDialog.Title>
          <AlertDialog.Description size="2">
            {currentConfig.description}
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                {currentConfig.cancelText}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color={currentConfig.confirmColor}
                onClick={handleAction}
              >
                {currentConfig.confirmText}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
}
